import type { FormikHelpers } from 'formik';
import { memo, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Image, Row } from 'react-bootstrap';
import 'glightbox/dist/css/glightbox.min.css';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import type { ProductEditType } from '../../types';
import SolarIcon from 'components/icons/SolarIcon';

const previewTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'bmp', 'svg'];

const getExtensionFromUrl = (url: string): string => {
    try {
        const pathname = new URL(url).pathname || '';
        return pathname.split('.').pop()?.toLowerCase() || '';
    } catch {
        return url.split('.').pop()?.split('?')[0]?.toLowerCase() || '';
    }
};

const getDisplayNameFromUrl = (url: string): string => {
    try {
        const pathname = new URL(url).pathname || '';
        return decodeURIComponent(pathname.split('/').pop() || url);
    } catch {
        return url;
    }
};

interface ProductFormImagesListProps {
    setFieldValue: FormikHelpers<ProductEditType>['setFieldValue'];
    values: ProductEditType;
    onDeleteImage?: (src: string, nextProdImages: string) => Promise<boolean>;
}

const ProductFormImagesList = memo(
    ({ setFieldValue, values, onDeleteImage }: ProductFormImagesListProps) => {
        const { auth } = useAuth();
        const canEdit = hasUserAccess(auth?.user, 'prodEdit');
        const [deletingSrc, setDeletingSrc] = useState<string | null>(null);

        const allSrc = useMemo(
            () => values?.prodImages?.split(';')?.filter((src) => src !== '') || [],
            [values?.prodImages],
        );
        const imageSrcs = useMemo(
            () => allSrc.filter((src) => previewTypes.includes(getExtensionFromUrl(src))),
            [allSrc],
        );
        const otherSrcs = useMemo(
            () => allSrc.filter((src) => !previewTypes.includes(getExtensionFromUrl(src))),
            [allSrc],
        );

        const handleClickDelete = async (deleteSrc: string) => {
            if (!deleteSrc) return;
            const prodImages = values?.prodImages?.split(';')?.filter((src) => src !== deleteSrc).join(';') || '';

            if (onDeleteImage) {
                setDeletingSrc(deleteSrc);
                const stat = await onDeleteImage(deleteSrc, prodImages);
                setDeletingSrc(null);
                if (!stat) return;
            }
            setFieldValue('prodImages', prodImages);
        };

        useEffect(() => {
            const GLightbox = require('glightbox');
            const lightbox = GLightbox({
                selector: '.glightbox',
            });

            return () => lightbox.destroy();
        }, [imageSrcs]);

        const galleryId = `product-images-${values?.ID || 'new'}`;

        if (!imageSrcs.length && !otherSrcs.length) return null;

        return (
            <>
                {imageSrcs.length > 0 && (
                    <Row>
                        {imageSrcs.map((src) => (
                            <Col className="p-1" xs={6} md={4} key={src}>
                                <Card>
                                    <Card.Body className="p-1" style={{ minHeight: '40px' }}>
                                        {canEdit && (
                                            <div style={{ position: 'absolute', right: '4px' }}>
                                                <Button
                                                    variant="light-danger"
                                                    size="sm"
                                                    disabled={deletingSrc === src}
                                                    onClick={() => handleClickDelete(src)}
                                                >
                                                    <SolarIcon name="trash3" />
                                                </Button>
                                            </div>
                                        )}
                                        <a
                                            className="glightbox"
                                            href={src}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-gallery={galleryId}
                                        >
                                            <img className="img-fluid rounded" src={src} alt="" />
                                        </a>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                {otherSrcs.length > 0 && (
                    <div
                        className="_form-control bg-body mb-2 p-2 d-flex flex-wrap rounded mt-2 text-start"
                        style={{ gap: '10px' }}
                    >
                        <ul className="list-inline mb-0">
                            {otherSrcs.map((src) => {
                                const displayName = getDisplayNameFromUrl(src);
                                return (
                                    <ProductFormAttachFile
                                        key={src}
                                        publicUrl={src}
                                        displayName={displayName}
                                        canDelete={canEdit}
                                        deleting={deletingSrc === src}
                                        onDelete={() => handleClickDelete(src)}
                                    />
                                );
                            })}
                        </ul>
                    </div>
                )}
            </>
        );
    },
    (prevProps, nextProps) =>
        prevProps?.values?.prodImages === nextProps?.values?.prodImages &&
        prevProps?.onDeleteImage === nextProps?.onDeleteImage,
);

interface ProductFormAttachFileProps {
    publicUrl: string;
    displayName: string;
    canDelete?: boolean;
    deleting?: boolean;
    onDelete?: () => void;
}

const ProductFormAttachFile = ({ publicUrl, displayName, canDelete, deleting, onDelete }: ProductFormAttachFileProps) => {
    const extension = displayName.split('.').pop()?.toLowerCase() || '';
    const [src, setSrc] = useState(`/imgs/fileTypes/${extension?.toLowerCase()}.png`);

    return (
        <li className="list-inline-item" style={{ maxWidth: '250px' }}>
            <Card className="p-1 my-1 text-truncate" style={{ borderRadius: '8px' }} title={displayName}>
                <Card.Body className="text-truncate p-0 d-flex align-items-center">
                    <Image
                        className="rounded img-fluid me-1"
                        style={{ height: '24px', padding: '2px' }}
                        onError={() => setSrc('/imgs/fileTypes/unknown.png')}
                        src={src}
                    />
                    <span className="text-truncate fs-12" style={{ paddingRight: canDelete ? '30px' : '10px' }}>
                        <a target="_blank" href={publicUrl} rel="noreferrer">
                            {displayName}
                        </a>
                    </span>
                    {canDelete && (
                        <div
                            style={{
                                position: 'absolute',
                                right: '8px',
                                opacity: '0.8',
                            }}
                        >
                            <Button
                                className="px-1 py-0"
                                variant="light-danger"
                                size="sm"
                                disabled={deleting}
                                onClick={onDelete}
                            >
                                <SolarIcon name="trash3" className="fs-12" />
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </li>
    );
};

export default ProductFormImagesList;
